import { CancelButton, FormModal, SubmitButton } from '../../../../Form';
import React, { useCallback } from 'react';
import { TextInput, YesNoRadioButtonGroup } from '../../../../Inputs';
import { Formik } from 'formik';
import { useAddendumForm } from './use-addendum-form.hook';
import { ProviderNavigation } from '../../../../Layout/ProviderLayout/ProviderNavigation';
import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ScrollableView, StickyStackItem } from '../../../../Layout';
import { SessionNoteDetailSidebar } from './session-note-detail-sidebar.component';
import { useUserAvatarAssetsQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { Modal, ModalActions } from '../../../../Modal';
import { Button } from '../../../../Button';
import { Session } from '@uplift/api';
import { TimeRange } from '@uplift/types';


export type AddendumModalProps = {
  open: boolean;
  onClose: () => void;
  session: Session;
  patientId: string;
  noteTime: TimeRange;
  mentalStatusExamNote?: string;
};

export const AddendumModal: React.VFC<AddendumModalProps> = ({ open, onClose, session, patientId, noteTime, mentalStatusExamNote }) => {
  const me = useCurrentuser();
  const [ deleteOpen, setDeleteOpen ] = React.useState(false);
  const { initialValues, validationSchema, onSubmit } = useAddendumForm(session?.id);
  const { data: avatar } = useUserAvatarAssetsQuery(me.id);
  const { classes } = useStyles();

  const handleSubmit = useCallback(values => {
    onSubmit(values);
    onClose();
  }, [ onClose, onSubmit ]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnMount={true}
    >
      <FormModal
        open={open}
        fullScreen
        onClose={() => setDeleteOpen(true)}
      >
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%" bgcolor="grey.50">
          <ProviderNavigation
            showProfile
            showLinks={false}
            showClientsSearch={false}
            avatarSrc={avatar?.avatarAsset.url}
          />
          <Box display="flex" flexDirection="row" height="100%" flex={1}>
            <ScrollableView>
              <Box className={classes.formContainer}>
                <Box className={classes.form}>
                  <Box pb={4}>
                    <Typography variant="h3">Addendum</Typography>
                  </Box>
                  <Box pb={4}>
                    <TextInput
                      name="content"
                      title="Addendum"
                      label="Specify additions or changes"
                      color="secondary"
                      multiline
                      rows={14}
                      fullWidth
                      inputProps={{ size: 14 }}
                    />
                  </Box>
                  <Box>
                    <YesNoRadioButtonGroup
                      name="submittedBillingUpdate"
                      label="Does this include changes to the CPT or Diagnosis code?"
                      asBoolean
                    />
                  </Box>
                </Box>
              </Box>
            </ScrollableView>
            <SessionNoteDetailSidebar
              session={session}
              patientId={patientId}
              mentalStatusExamNote={mentalStatusExamNote ?? ''}
              noteTime={noteTime}
            />
          </Box>
          <StickyStackItem order={0} placement="bottom">
            <>
              <Divider />
              <Box boxSizing="border-box" p={2} display="flex" justifyContent="space-between">
                <Modal open={deleteOpen} onClose={onClose} maxWidthPx="300px" fullWidth>
                  <Box p={2}>
                    <Typography variant="h6" align="center">Delete this addendum?</Typography>
                  </Box>
                  <ModalActions>
                    <Button fullWidth onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button
                      fullWidth variant="contained" color="error" onClick={() => {
                        setDeleteOpen(false);
                        onClose();
                      }}
                    >Delete</Button>
                  </ModalActions>
                </Modal>
                <CancelButton onClick={() => setDeleteOpen(true)} variant="outlined" color="secondary">
                  Delete
                </CancelButton>
                <SubmitButton color="secondary">
                  Sign and Submit
                </SubmitButton>
              </Box>
            </>
          </StickyStackItem>
        </Box>
      </FormModal>
    </Formik>
  );
};

const useStyles = makeStyles()((theme) => ({
  formContainer: {
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '648px',
    width: '100%',
  },
  textInput: {
    height: '280px'
  }
}));
